import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { CtuJointOutcome } from '../../types/api';

interface JointOutcomesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  outcomes: CtuJointOutcome[];
}

export default function JointOutcomesSheet({
  open,
  onOpenChange,
  companyName,
  outcomes,
}: JointOutcomesSheetProps) {
  const { t } = useTranslation('ctuCompanies');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col gap-0 p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle>{t('jointOutcomes.title')}</SheetTitle>
          <SheetDescription>
            {t('jointOutcomes.description', { company: companyName })}
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <ScrollArea className="flex-1">
          <div className="px-6 py-6">
            {outcomes.length === 0 ? (
              <p className="text-center text-sm text-ctu-text-muted py-10">
                {t('jointOutcomes.empty')}
              </p>
            ) : (
              <ol className="m-0 flex flex-col divide-y divide-border list-none p-0">
                {outcomes.map((o) => (
                  <li key={o.id} className="py-3 flex flex-col gap-1">
                    <a
                      href={`/outcome/${o.id}?from=ctu-companies`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary font-medium text-sm underline-offset-2 hover:underline"
                    >
                      {o.name || `#${o.id}`}
                    </a>
                    <div className="flex items-center gap-2 text-xs text-ctu-text-muted">
                      {o.type && <span>{o.type}</span>}
                      {o.type && o.year != null && <span aria-hidden>·</span>}
                      {o.year != null && <span className="tabular-nums">{o.year}</span>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
